<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import { ProjectPlanEditorFormSubmitEventPayload } from '@Quest/ProjectPlans/Components/ProjectPlanEditor'
    import type { ProjectPlanEditorViewModel } from '@Quest/ProjectPlans/Components/ProjectPlanEditor'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: ProjectPlanEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'form-submit', payload: ProjectPlanEditorFormSubmitEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const confirmButtonLabel = computed(() => vm.value.confirmButtonLabel)
    const modalTitle = computed(() => vm.value.modalTitle)

    // writable computeds (replace getter + setter pairs)

    const name = computed<string>({
        get: () => vm.value.name,
        set: v => { vm.value.name = v }
    })

    const granularity = computed<string>({
        get: () => vm.value.granularity,
        set: v => { vm.value.granularity = v }
    })

    const denomination = computed<string>({
        get: () => vm.value.denomination,
        set: v => { vm.value.denomination = v }
    })

    const duration = computed<number>({
        get: () => vm.value.duration,
        set: v => { vm.value.duration = v }
    })


    const dueDate = computed<string | null>({
        get: () => vm.value.dueDate,
        set: v => { vm.value.dueDate = v }
    })

    // ---------------- actions ----------------

    function onFormSubmit()
    {
        emit(
            'form-submit',
            new ProjectPlanEditorFormSubmitEventPayload(
                vm.value.id,
                name.value,
                duration.value,
                granularity.value,
                denomination.value,
                dueDate.value
            )
        )
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <form id="project-plan-editor-form" @submit.prevent="onFormSubmit">
                            <div class="form-group">
                                <label>Name:</label>
                                <input type="text" v-model.trim="name" class="form-control" required minlength="2" maxlength="100" />
                            </div>

                            <div class="form-group">
                                <label for="account">Granularity:</label>
                                <select id="account" v-model="granularity" class="form-control">
                                    <option value="Monthly">Monthly</option>
                                    <option value="Quarterly">Quarterly</option>
                                    <option value="Yearly">Yearly</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label for="denomination">Currency denomination:</label>
                                <select id="denomination" v-model="denomination" class="form-control">
                                    <option value="Unit">Unit</option>
                                    <option value="Thousand">Thousand</option>
                                    <option value="Million">Million</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label>Duration (in months):</label>
                                <input type="number" v-model.number="duration" class="form-control" required min="1" step="1" />
                            </div>

                            <div class="form-group">
                                <label>Submission due date:</label>
                                <input type="date" class="form-control" v-model="dueDate" />
                            </div>

                        </form>

                    </div>
                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="project-plan-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>